import React, { useState, useEffect } from 'react';
import QRCode from 'react-qr-code';
import { useNavigate } from 'react-router-dom';
import { getDatabase, ref as dbRef, remove, get } from "firebase/database";
import { getAuth } from 'firebase/auth';
import { motion, AnimatePresence } from 'framer-motion';
import './AddPageComponent.css';
import './Demo/Layouts.css';

// Lokale Page Preview Komponente
const LocalPagePreview = ({ pageData }) => {
    // Importiere die Widget-Komponenten (alle verbleibenden Widgets)
    const ProductInfoWidget = React.lazy(() => import('./WidgetTypes/ProductInfoWidget'));
    const HeroHeader = React.lazy(() => import('./WidgetTypes/HeroHeader'));
    const HiWidget = React.lazy(() => import('./WidgetTypes/HiWidget'));
    const VideoTeaser = React.lazy(() => import('./WidgetTypes/VideoTeaser'));
    const SimplisticHeader = React.lazy(() => import('./WidgetTypes/SimplisticHeader'));
    const SupremeTestimonial = React.lazy(() => import('./WidgetTypes/SupremeTestimonial'));
    const SimplisticQuote = React.lazy(() => import('./WidgetTypes/SimplisticQuote'));
    const MethodologyWidget = React.lazy(() => import('./WidgetTypes/MethodologyWidget'));
    const PersonalBrandingWidget = React.lazy(() => import('./WidgetTypes/PersonalBrandingWidget'));
    const SimplisticTestimonial = React.lazy(() => import('./WidgetTypes/SimplisticTestimonial'));
    const ProductFormPopup = React.lazy(() => import('./WidgetTypes/ProductFormPopup'));
    const EventInfoWidget = React.lazy(() => import('./WidgetTypes/EventInfoWidget'));
    const CourseInfoWidget = React.lazy(() => import('./WidgetTypes/CourseInfoWidget'));
    const InfiniteBanner = React.lazy(() => import('./WidgetTypes/InfiniteBanner'));
    const QAWidget = React.lazy(() => import('./WidgetTypes/QAWidget'));
    const HolisticTestimonial = React.lazy(() => import('./WidgetTypes/HolisticTestimonial'));
    const SimplisticServices = React.lazy(() => import('./WidgetTypes/SimplisticServices'));
    const BulletPointsWidget = React.lazy(() => import('./WidgetTypes/BulletPointsWidget'));
    const ComparisonWidget = React.lazy(() => import('./WidgetTypes/ComparisonWidget'));
    const BragBar = React.lazy(() => import('./WidgetTypes/BragBar'));
    const SpiritualFeatureBanner = React.lazy(() => import('./WidgetTypes/SpiritualFeatureBanner'));
    const AchievementsBanner = React.lazy(() => import('./WidgetTypes/AchievementsBanner'));
    const SpiritualQuoteCard = React.lazy(() => import('./WidgetTypes/SpiritualQuoteCard'));
    const FancyFooter = React.lazy(() => import('./WidgetTypes/FancyFooter'));

    const renderWidget = (widget) => {
        if (!widget || !widget.type) {
            return null;
        }
        
        const props = { data: widget, isEditMode: false };
        
        switch (widget.type) {
            case 'ProductInfoWidget': return <ProductInfoWidget {...props} />;
            case 'HeroHeader': return <HeroHeader {...props} />;
            case 'HiWidget': return <HiWidget {...props} />;
            case 'VideoTeaser': return <VideoTeaser {...props} />;
            case 'SimplisticHeader': return <SimplisticHeader {...props} />;
            case 'SupremeTestimonial': return <SupremeTestimonial {...props} />;
            case 'SimplisticQuote': return <SimplisticQuote {...props} />;
            case 'MethodologyWidget': return <MethodologyWidget {...props} />;
            case 'PersonalBrandingWidget': return <PersonalBrandingWidget {...props} />;
            case 'SimplisticTestimonial': return <SimplisticTestimonial {...props} />;
            case 'SimplisticServices': return <SimplisticServices {...props} />;
            case 'BulletPointsWidget': return <BulletPointsWidget {...props} />;
            case 'ComparisonWidget': return <ComparisonWidget {...props} />;
            case 'ProductFormPopup': return <ProductFormPopup {...props} />;
            case 'EventInfoWidget': return <EventInfoWidget {...props} />;
            case 'CourseInfoWidget': return <CourseInfoWidget {...props} />;
            case 'QAWidget': return <QAWidget {...props} />;
            case 'HolisticTestimonial': return <HolisticTestimonial {...props} />;
            case 'BragBar': return <BragBar {...props} />;
            case 'SpiritualFeatureBanner': return <SpiritualFeatureBanner {...props} />;
            case 'AchievementsBanner': return <AchievementsBanner {...props} />;
            case 'SpiritualQuoteCard': return <SpiritualQuoteCard {...props} />;
            case 'InfiniteBanner': return <InfiniteBanner {...props} />;
            case 'FancyFooter': return <FancyFooter {...props} />;
            default: 
                return (
                    <div style={{ padding: '10px', background: '#f0f0f0', borderRadius: '8px', margin: '5px' }}>
                        <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                            Unknown widget: {widget.type}
                        </p>
                    </div>
                );
        }
    };

    const pageBackgroundColor = pageData.backgroundColor || '#ffffff';
    
    // Handle both Array and Object widget structures
    let sortedWidgets = [];
    if (pageData.widgets) {
        if (Array.isArray(pageData.widgets)) {
            // Widgets as Array - sortiert nach position-Property
            sortedWidgets = pageData.widgets.sort((a, b) => (a.position || 0) - (b.position || 0));
        } else {
            // Widgets as Object - sortiert nach position-Property
            sortedWidgets = Object.values(pageData.widgets).sort((a, b) => (a.position || 0) - (b.position || 0));
        }
    }

    return (
        <div className="local-page-preview" style={{ 
            width: '100%', 
            height: '690px', 
            overflow: 'auto',
            backgroundColor: pageBackgroundColor,
            borderRadius: '25px',
            border: '1px solid white',
            boxShadow: 'rgba(50, 50, 93, 0.25) 0px 50px 100px -20px, rgba(0, 0, 0, 0.3) 0px 30px 60px -30px'
        }}>
            <React.Suspense fallback={<div style={{ padding: '20px' }}>Loading...</div>}>
                {sortedWidgets.map((widget, index) => (
                    <div key={index} className="widget-boss-wrapper" style={{ backgroundColor: pageBackgroundColor }}>
                        {renderWidget(widget)}
                    </div>
                ))}
                {sortedWidgets.length === 0 && (
                    <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                        <p>No widgets added yet</p>
                    </div>
                )}
            </React.Suspense>
        </div>
    );
};

const PagesDisplay = ({ pages, setPages, activePageIndex: propActivePageIndex, setActivePageIndex: propSetActivePageIndex }) => {
    const [focusedCardId, setFocusedCardId] = useState(null);
    const [deleteModalShow, setDeleteModalShow] = useState(false);
    const [pageToDelete, setPageToDelete] = useState(null);
    
    // Verwende Props wenn verfügbar, sonst lokalen State
    const activePageIndex = propActivePageIndex !== undefined ? propActivePageIndex : 0;
    const setActivePageIndex = propSetActivePageIndex || (() => {});
    
    // Debug logging for activePageIndex changes
    useEffect(() => {
        console.log('🔄 PagesDisplay: activePageIndex changed to:', activePageIndex);
    }, [activePageIndex]);

    // Simple fade-in animation for pages slider (no transform conflicts)
    const sliderVariants = {
        hidden: { 
            opacity: 0
        },
        visible: { 
            opacity: 1,
            transition: {
                duration: 0.2,
                ease: "easeOut"
            }
        }
    };
    const navigate = useNavigate();
    const db = getDatabase();
    const auth = getAuth();
    const currentUser = auth.currentUser;

    const openPage = (page) => {
        console.log('🔍 PagesDisplay: Opening page for edit:', { 
            id: page.id, 
            pageName: page.pageName, 
            slug: page.slug, 
            isHomePage: page.isHomePage 
        });
        
        // Check if page has new structure (id contains slashes)
        if (page.id && page.id.includes('/')) {
            // New structure: userId/spaceId/pageName or userId/pageName
            const pathParts = page.id.split('/');
            if (pathParts.length === 3) {
                // 3-level: userId/spaceId/pageName - use pageName (internal key)
                const pageKey = page.pageName || (page.isHomePage ? 'home' : pathParts[2]);
                navigate(`/settings/page/${pathParts[0]}/${pathParts[1]}/${pageKey}`);
            } else if (pathParts.length === 2) {
                // 2-level: userId/pageName
                navigate(`/settings/page/${pathParts[0]}/${pathParts[1]}`);
            } else {
                // Fallback to old structure
                navigate(`/settings/page/${page.id}`);
            }
        } else {
            // Old structure
            navigate(`/settings/page/${page.id}`);
        }
    };

    const promptDeletePage = (pageId) => {
        setPageToDelete(pageId);
        setDeleteModalShow(true);
    };

    const confirmDeletePage = () => {
        if (!currentUser || !pageToDelete) {
            console.error('Fehler: Kein Benutzer angemeldet oder keine Seite ausgewählt.');
            return;
        }
        // Referenz zur Seite, die gelöscht werden soll
        const pageRef = dbRef(db, `/pages/${pageToDelete}`);
        // Zuerst die Seite abrufen, um die benutzerdefinierte Route zu erhalten
        get(pageRef).then(snapshot => {
            if (snapshot.exists()) {
                const pageData = snapshot.val();
                // Referenzen zu den entsprechenden Einträgen in `publicPages`
                const customRouteRef = dbRef(db, `/publicPages/${pageData.customDomain}`);
                const pageIdRef = dbRef(db, `/publicPages/${pageToDelete}`);
                // Lösche den Eintrag basierend auf der customDomain
                return remove(customRouteRef).then(() => {
                    // Lösche den Eintrag basierend auf der pageId
                    return remove(pageIdRef);
                }).then(() => {
                    // Nach erfolgreichem Löschen der Einträge in `publicPages`, die Seite selbst löschen
                    return remove(pageRef);
                });
            } else {
                console.error('Seite nicht gefunden.');
                return Promise.reject('Seite nicht gefunden.');
            }
        })
        .then(() => {
            console.log("Seite und zugehörige Einträge in 'publicPages' erfolgreich gelöscht");
            // Aktualisiere den Seitenzustand, um die gelöschte Seite aus der Liste zu entfernen
            setPages(pages.filter(page => page.id !== pageToDelete));
            // Schließe das Modal zum Löschen
            setDeleteModalShow(false);
        })
        .catch(error => {
            console.error("Fehler beim Löschen der Seite und der zugehörigen Einträge in 'publicPages':", error);
        });
        // Setze `pageToDelete` zurück
        setPageToDelete(null);
    };

    return (
        <>
            {deleteModalShow && (
                <div className="modal-background">
                    <div className="modal-content" style={{ height: 350 }}>
                        <h2>Really want to delete this masterpiece?</h2>
                        <div className="qr-buttons">
                            <button className="qr-modal-open" onClick={confirmDeletePage}>Yes, delete</button>
                            <button className="qr-modal-close" onClick={() => setDeleteModalShow(false)}>Close</button>
                        </div>
                    </div>
                </div>
            )}
            <div className="pages-horizontal-container">
                {/* Page Content with Horizontal Slide Animation */}
                <div 
                    className="pages-content-container"
                    style={{ '--total-pages': pages.length }}
                >
                    <motion.div 
                        className="pages-slider"
                        variants={sliderVariants}
                        initial="hidden"
                        animate="visible"
                        style={{ 
                            transform: `translateX(-${(activePageIndex * 100) / pages.length}%)`,
                            '--total-pages': pages.length
                        }}
                        onAnimationComplete={() => {
                            console.log('🎬 Slider animation completed for index:', activePageIndex);
                        }}
                    >
                        {pages.map((page, index) => (
                            <div key={page.id || `slide-${index}`} className="page-slide">
                            <div className="page-slide-header">
                                <div className="page-slide-info">
                                    <h3>{page.name}</h3>
                                    {page.viewCount > 0 && (
                                        <div className="page-slide-stats">
                                            <i className="fas fa-eye"></i>
                                            <span>{page.viewCount} views</span>
                                        </div>
                                    )}
                                </div>
                                <div className="page-slide-actions">
                                    <button 
                                        className="primary-edit-btn" 
                                        onClick={() => {
                                            console.log('🔍 PagesDisplay: Edit Page clicked for:', page);
                                            openPage(page);
                                        }}
                                        title="Edit Page"
                                    >
                                        <div className="edit-btn-icon-container">
                                            <i className="fas fa-edit"></i>
                                        </div>
                                        <span className="edit-btn-text">Edit Page</span>
                                    </button>
                                    <button 
                                        className="page-action-btn delete-btn" 
                                        onClick={(e) => { e.stopPropagation(); promptDeletePage(page.id); }}
                                        title="Delete Page"
                                    >
                                        <i className="fas fa-trash-alt"></i>
                                    </button>
                                </div>
                            </div>
                            <div className="page-slide-preview">
                                <LocalPagePreview pageData={page} />
                            </div>
                            </div>
                        ))}
                    </motion.div>
                </div>
            </div>
        </>
    );
};

export default PagesDisplay;
